import styled from 'styled-components/macro'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CarEntryHeader from 'components/molecules/car-entry-header'
import {
  Root as TabsRoot,
  List as TabsList,
  Content as TabsContent,
} from 'components/layouts/simple-tabs'
import propTypes from 'prop-types'
import Typography from 'components/molecules/typography'
import Overlay from 'components/molecules/overlay'
import { media } from 'utilities/styled'
import MarktplaatsDetailActions from 'components/molecules/marktplaats-detail-actions'
import { CarFileViewTypeEnum } from 'config/enums'
import * as routes from 'config/routes'
import AdvertisementDetailActions from './advertisement-detail-actions'

const StyledOverlay = styled(Overlay)`
  z-index: 1320; // above MobileActionBarButton (z-index: 1310)

  .child-container {
    flex-direction: column;
    overflow: hidden;
  }
`

const StyledCarEntryHeader = styled(CarEntryHeader)`
  height: ${({ theme }) => theme.sizeByFactor(10.5)};
  width: 100%;
  background-color: ${({ theme }) => theme.colors.brandGolf};
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};

  img {
    height: ${({ theme }) => theme.sizeByFactor(10.5)};
    width: ${({ theme }) => theme.sizeByFactor(14)};
    object-fit: cover;
    flex-shrink: 0;
  }

  figure {
    height: ${({ theme }) => theme.sizeByFactor(10.5)};
    width: auto;
    flex-shrink: 0;
    padding-top: 0;
  }

  > div:first-child {
    min-width: 0;
  }

  [data-test-e2e='car-title'] {
    overflow: hidden;
    min-width: 0;

    > * {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  ${media.desktop`
    margin-bottom: 0;

    [data-test-e2e="car-title"] {
      overflow: visible;

      > * {
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
      }
    }
  `}
`

const StyledTabsList = styled(TabsList)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.brandDelta};

  .ItemContainer {
    button[role='tab'] {
      background: transparent;
      padding-left: 0;
      padding-right: 0;
      padding-top: ${({ theme }) => theme.sizeByFactor(3.5)};
      margin-right: ${({ theme }) => theme.sizings.lvl4};

      &[aria-selected='true'] {
        padding-bottom: ${({ theme }) => theme.sizeByFactor(1.5)};
        border-bottom: ${({ theme }) => theme.sizings.lvl0} solid
          ${({ theme }) => theme.colors.brandAlfa};
      }
    }

    select {
      height: ${({ theme }) => theme.sizeByFactor(6.5)};
    }
  }
`

const StyledTabsRoot = styled(TabsRoot)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`

const StyledTabsContent = styled(TabsContent)`
  background: transparent;
  overflow: auto;
  max-height: none;
  flex: 1 1 auto;
`

function MarktplaatsViewOverlay({
  onClose,
  image,
  brand,
  model,
  type,
  licensePlate,
  mileage,
  year,
  sellingPrice,
  marktplaatsData,
  updateMarktplaatsFeatures,
  getMarktplaats,
  carId,
  marktplaatsInsights,
  advertisementData,
  selectedViewType,
}) {
  const [activeTab, setActiveTab] = useState(selectedViewType)
  const { t } = useTranslation()

  useEffect(() => {
    if (carId) {
      getMarktplaats({ auto_id: carId })
    }
  }, [carId, getMarktplaats])

  const handleMarktplaatsSubmit = async (values) => {
    const payload = {
      ...marktplaatsData,
      ...values,
    }
    try {
      await updateMarktplaatsFeatures([payload])
    } catch (error) {
      console.error('Failed to update Marktplaats features:', error)
    }
  }

  const handleAdvertisementSubmit = async (values) => {
    // TODO: Implement advertisement submit
  }

  return (
    <StyledOverlay
      close={onClose}
      title={t('marktplaatsView.title')}
      data-test-e2e="marktplaats-view-overlay"
      icon="display-mode-list"
    >
      <StyledCarEntryHeader
        imagePath={image}
        brand={brand}
        model={model}
        type={type}
        licensePlate={licensePlate}
        mileage={mileage}
        year={year}
        carId={carId}
        carDetailsSummary
        labeledPriceSummary
        hideLocationAndKind
        carfile={{ data: { sellingprice: sellingPrice, taxMargin: 'marge' } }}
        buttonIcon="pen"
        buttonTo={`${routes.CAR_FILE}/${carId}`}
        showImageOnMobile
      />
      <StyledTabsRoot value={activeTab} onValueChange={(val) => setActiveTab(val)}>
        <StyledTabsList
          items={[
            {
              label: t('advertisements'),
              value: CarFileViewTypeEnum.ADVERTISEMENT,
            },
            { label: t('marktplaats'), value: CarFileViewTypeEnum.MARKTPLAATS },
          ]}
        />

        <StyledTabsContent value={CarFileViewTypeEnum.ADVERTISEMENT}>
          <AdvertisementDetailActions
            onSubmit={handleAdvertisementSubmit}
            carId={carId}
          />
        </StyledTabsContent>

        <StyledTabsContent value={CarFileViewTypeEnum.MARKTPLAATS}>
          <Typography type="Level1Heading">Marktplaats</Typography>
          <MarktplaatsDetailActions
            initialData={marktplaatsData}
            onSubmit={handleMarktplaatsSubmit}
            marktplaatsInsights={marktplaatsInsights}
            carId={carId}
          />
        </StyledTabsContent>
      </StyledTabsRoot>
    </StyledOverlay>
  )
}

MarktplaatsViewOverlay.propTypes = {
  onClose: propTypes.func,
  image: propTypes.string,
  brand: propTypes.string,
  model: propTypes.string,
  type: propTypes.string,
  licensePlate: propTypes.string,
  mileage: propTypes.string,
  year: propTypes.number,
  sellingPrice: propTypes.number,
  carId: propTypes.string.isRequired,
  marktplaatsData: propTypes.shape({
    id: propTypes.string,
    prijstype: propTypes.string,
    minimaal_bod: propTypes.string,
    omhoogplaatsen: propTypes.bool,
    has_dagtopper: propTypes.bool,
    has_dagtopper7: propTypes.bool,
    has_blikvanger: propTypes.bool,
    has_opvalsticker: propTypes.bool,
    herplaatsen: propTypes.bool,
    herplaatsen_aantal: propTypes.number,
    opvaltekst: propTypes.string,
  }),
  updateMarktplaatsFeatures: propTypes.func.isRequired,
  getMarktplaats: propTypes.func.isRequired,
  marktplaatsInsights: propTypes.object,
  selectedViewType: propTypes.string,
  advertisementData: propTypes.object,
}

MarktplaatsViewOverlay.defaultProps = {
  marktplaatsOverlayOpen: false,
  marktplaatsData: null,
  marktplaatsInsights: null,
  selectedViewType: null,
  advertisementData: null,
  year: undefined,
  sellingPrice: null,
}

export default MarktplaatsViewOverlay
